

import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert, Stack, Divider
} from '@mui/material';
import { getPublicOpenJobs } from '../../../../services/api';

const formatSalaryValue = (value) => {
  if (value === '' || value === null || value === undefined) return '';

  const numericValue = typeof value === 'number'
    ? value
    : Number(String(value).replace(/\./g, '').replace(',', '.'));

  if (!Number.isFinite(numericValue)) return String(value);

  return numericValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const getSalaryText = (job) => {
  const salaryFloor = formatSalaryValue(job?.salary_floor);
  const salaryCeiling = formatSalaryValue(job?.salary_ceiling);

  if (salaryFloor && salaryCeiling) return `${salaryFloor} a ${salaryCeiling}`;
  return salaryFloor || salaryCeiling || '';
};

/**
 * Componente de página (pública) que exibe a lista de vagas abertas
 * para um restaurante específico, com base em um 'slug' na URL.
 * @returns {JSX.Element}
 */
export default function PublicJobs() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  /**
   * Busca os dados das vagas públicas (nome do restaurante e lista de vagas)
   * com base no slug da URL.
   */
  const load = useCallback(async () => {
    if (!slug) {
      setError('Slug do restaurante não fornecido.');
      setLoading(false);
      return;
    }
    try {
      const result = await getPublicOpenJobs(slug);
      setData(result);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível carregar as vagas.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!data) return null; // Se não houver erro, mas os dados forem nulos (raro)

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2, my: 4 }}>
      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom>Vagas em Aberto</Typography>
        <Typography variant="h6" color="text.secondary" mb={3}>{data.restaurantName}</Typography>
        
        <Stack divider={<Divider />} spacing={0}>
          {data.jobs.length > 0 ? (
            data.jobs.map(job => {
              const salaryText = getSalaryText(job);

              return (
              <Box key={job.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, '&:hover': { bgcolor: '#f5f5f5' } }}>
                <Box>
                  <Typography variant="h6">{job.title}</Typography>
                  {salaryText ? (
                    <Typography variant="body2" color="text.secondary">{salaryText}</Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary">{job.area || ''}</Typography>
                </Box>
                <Button
                  variant="contained"
                  component={Link}
                  to={`/cv/aplicar/${job.id}`}
                >
                  Candidatar-se
                </Button>
              </Box>
            );
            })
          ) : (
            <Typography sx={{ p: 3, textAlign: 'center' }} color="text.secondary">
              Nenhuma vaga aberta no momento.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}